import {
  Component,
  ContentChild,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Carousel } from 'primeng/carousel';

export interface CarouselCardItem {
  name: string;
  image: string;
  [key: string]: any;
}

const ITEM_GUTTER_REM = 1.25;

@Component({
  selector: 'app-carousel-cards',
  templateUrl: './carousel-cards.component.html',
  styleUrls: ['./carousel-cards.component.scss']
})
export class CarouselCardsComponent implements OnInit, OnDestroy {
  @Input() items: any[] = [];
  @Input() set visibleItems(value: number) {
    this.maxVisible = Math.min(Math.max(value, 3), 6);
    this.numVisible = this.maxVisible;
    this.responsiveOptions = this.buildResponsiveOptions();
  }
  @Input() set mobileVisibleItems(value: number | undefined) {
    this.mobileVisible = value;
    this.responsiveOptions = this.buildResponsiveOptions();
  }
  @Input() minItemWidth?: number;
  @Input() fadeEnd = false;
  @Output() itemSelected = new EventEmitter<any>();

  @ContentChild(TemplateRef) itemTemplate?: TemplateRef<any>;
  @ViewChild('carouselCards', { static: true }) carouselCards!: Carousel;

  maxVisible = 6;
  numVisible = 6;
  mobileVisible?: number;
  responsiveOptions: any[] | null = this.buildResponsiveOptions();
  private resizeObserver?: ResizeObserver;

  constructor(private readonly ngZone: NgZone) {}

  ngOnInit(): void {
    if (!this.minItemWidth) return;
    this.responsiveOptions = null;
    this.resizeObserver = new ResizeObserver(([entry]) =>
      this.ngZone.run(() => this.fitToWidth(entry.contentRect.width))
    );
    this.resizeObserver.observe(this.carouselCards.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  prevPage(): void {
    this.carouselCards.navBackward(new Event('click'));
  }

  nextPage(): void {
    this.carouselCards.navForward(new Event('click'));
  }

  isPrevDisabled(): boolean {
    return !this.carouselCards || this.carouselCards.isBackwardNavDisabled();
  }

  isNextDisabled(): boolean {
    return !this.carouselCards || this.carouselCards.isForwardNavDisabled();
  }

  selectItem(item: any): void {
    this.itemSelected.emit(item);
  }

  private fitToWidth(width: number): void {
    const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
    const slotWidth = (this.minItemWidth! + ITEM_GUTTER_REM) * rem;
    const fit = Math.floor(((width + ITEM_GUTTER_REM * rem) / slotWidth) * 10) / 10;
    this.numVisible = Math.max(1, Math.min(this.maxVisible, fit));
  }

  private buildResponsiveOptions() {
    return [
      { breakpoint: '1200px', numVisible: Math.min(this.maxVisible, 5), numScroll: 1 },
      { breakpoint: '992px', numVisible: Math.min(this.maxVisible, 4), numScroll: 1 },
      { breakpoint: '768px', numVisible: this.mobileVisible ? this.mobileVisible + 1 : 3, numScroll: 1 },
      { breakpoint: '576px', numVisible: this.mobileVisible ?? 2, numScroll: 1 }
    ];
  }
}
